import { useContext, useEffect, useId, useState, useSyncExternalStore } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import {
  CrossIcon,
  HelpIcon,
  LeftArrowIcon,
  RightArrowIcon,
} from '../../../assets/icons/icons';
import {
  formatTransactionFullDate,
  getLocalizedTransactionText,
  getTransactionById,
  getLocalizedTransactionCategory,
  getTransactionsSnapshot,
  loadTransactionById,
  subscribeTransactions,
} from '../../../assets/services/transactions';
import { triggerImpactHaptic } from '../../../assets/utils/hapticFeedback';
import BottomSheet from '../../shared/BottomSheet/BottomSheet';
import TransactionCardIdentity from '../../shared/TransactionCardIdentity/TransactionCardIdentity';
import TransactionIcon from '../../shared/TransactionIcon/TransactionIcon';
import './TransactionDetail.css';

const SKELETON_ROWS = ['date', 'currency', 'card', 'category'];

function MerchantNameInfo({ originalDescription }) {
  const { history } = useContext(TranslationContext);
  const shouldReduceMotion = useReducedMotion();
  const [isOpen, setIsOpen] = useState(false);
  const contentId = useId();
  const copy = history.merchantNameInfo;

  return (
    <section className="transaction-detail__merchant-info">
      <button
        type="button"
        className="transaction-detail__merchant-info-toggle"
        aria-expanded={isOpen}
        aria-controls={contentId}
        onClick={() => {
          triggerImpactHaptic('light');
          setIsOpen((current) => !current);
        }}
      >
        <HelpIcon className="transaction-detail__merchant-info-icon" />
        <span>{copy.title}</span>
        <RightArrowIcon className="transaction-detail__merchant-info-chevron" />
      </button>
      <motion.div
        id={contentId}
        className="transaction-detail__merchant-info-reveal"
        aria-hidden={!isOpen}
        initial={false}
        animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
        transition={{
          duration: shouldReduceMotion ? 0 : 0.24,
          ease: [0.22, 1, 0.36, 1],
        }}
      >
        <div className="transaction-detail__merchant-info-content">
          <p>{copy.explanation} {copy.compare}</p>
          <div className="transaction-detail__original-description">
            <span className="transaction-detail__original-description-label">
              {copy.originalDescription}
            </span>
            <p className="transaction-detail__original-description-value">
              {originalDescription || copy.unavailable}
            </p>
          </div>
          <p className="transaction-detail__merchant-info-next-step">{copy.unrecognized}</p>
        </div>
      </motion.div>
    </section>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="transaction-detail__row">
      <span className="transaction-detail__label">{label}</span>
      <span className="transaction-detail__value">{value}</span>
    </div>
  );
}

function CurrencyValue({ code, iconUrl, network }) {
  const [hasIconError, setHasIconError] = useState(false);

  return (
    <span className="transaction-detail__currency">
      {iconUrl && !hasIconError ? (
        <img
          className="transaction-detail__currency-icon"
          src={iconUrl}
          alt=""
          onError={() => setHasIconError(true)}
        />
      ) : null}
      <span>{network ? `${code} (${network})` : code}</span>
    </span>
  );
}

function TransactionDetailSkeleton({ label }) {
  return (
    <div
      className="transaction-detail__skeleton"
      aria-label={label}
      aria-busy="true"
    >
      <div className="transaction-detail__skeleton-hero" aria-hidden="true">
        <span className="transaction-detail__skeleton-shape transaction-detail__skeleton-icon" />
        <span className="transaction-detail__skeleton-shape transaction-detail__skeleton-title" />
        <span className="transaction-detail__skeleton-shape transaction-detail__skeleton-amount" />
      </div>

      <div className="transaction-detail__skeleton-card" aria-hidden="true">
        {SKELETON_ROWS.map((row) => (
          <div key={row} className="transaction-detail__skeleton-row">
            <span className="transaction-detail__skeleton-shape transaction-detail__skeleton-label" />
            <span className="transaction-detail__skeleton-shape transaction-detail__skeleton-value" />
          </div>
        ))}
      </div>
    </div>
  );
}

function getTransactionHash(transaction) {
  return (
    transaction.txHash ||
    transaction.tx_hash ||
    transaction.transactionHash ||
    transaction.transaction_hash ||
    transaction.hash ||
    ''
  );
}

function getBlockscannerUrl(transaction) {
  return (
    transaction.blockscannerUrl ||
    transaction.blockscanner_url ||
    transaction.blockScannerUrl ||
    transaction.block_scanner_url ||
    transaction.blockchainExplorerUrl ||
    transaction.blockchain_explorer_url ||
    transaction.explorerUrl ||
    transaction.explorer_url ||
    transaction.txUrl ||
    transaction.tx_url ||
    ''
  );
}

function openExternalLink(url) {
  if (!url) {
    return;
  }

  const webApp = window.Telegram?.WebApp;

  if (typeof webApp?.openLink === 'function') {
    webApp.openLink(url);
    return;
  }

  window.open(url, '_blank', 'noopener,noreferrer');
}

function isTopUpTransaction(transaction) {
  const operationType = String(
    transaction.operationType
    || transaction.raw?.type
    || ''
  ).toLowerCase();

  if (operationType.includes('refund') || transaction.iconVariant === 'refund') {
    return false;
  }

  const searchableText = [
    operationType,
    transaction.id,
    transaction.merchant,
    transaction.description,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return searchableText.includes('top up')
    || searchableText.includes('top_up')
    || searchableText.includes('topup')
    || searchableText.includes('deposit')
    || (transaction.category === 'topUp' && transaction.iconVariant === 'deposit');
}

function isPaymentCanceledTransaction(transaction) {
  const operationType = String(
    transaction?.operationType
    || transaction?.raw?.type
    || transaction?.type
    || ''
  ).trim().toLowerCase();

  return transaction?.iconVariant === 'payment-canceled'
    || operationType === 'payment_canceled';
}

function formatTxHash(hash) {
  const value = String(hash || '').trim();

  if (value.length <= 24) {
    return value;
  }

  return `${value.slice(0, 8)}...${value.slice(-8)}`;
}

function getStatusLabel(transaction, history) {
  if (isPaymentCanceledTransaction(transaction)) {
    return history.canceledStatus || history.paymentCanceled;
  }

  const status = String(transaction?.status || '').toLowerCase();

  if (status === 'finished') {
    return '';
  }

  if (status.includes('fail') || status.includes('decline')) {
    return history.failed;
  }

  if (status.includes('pending') || status.includes('process')) {
    return history.pending;
  }

  return history.completedStatus;
}

function TransactionDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { common, deposit, history, language, t } = useContext(TranslationContext);
  useSyncExternalStore(
    subscribeTransactions,
    getTransactionsSnapshot,
    getTransactionsSnapshot
  );
  const routeTransaction = location.state?.transaction;
  const transactionFromRoute = String(routeTransaction?.id || '') === String(id || '')
    ? routeTransaction
    : null;
  const transaction = transactionFromRoute || getTransactionById(id);
  const [isTransactionLoading, setIsTransactionLoading] = useState(!transaction);
  const isSheet = Boolean(location.state?.backgroundLocation);

  useEffect(() => {
    if (!id || transaction) {
      setIsTransactionLoading(false);
      return undefined;
    }

    let isActive = true;

    setIsTransactionLoading(true);
    loadTransactionById(id)
      .catch(() => null)
      .finally(() => {
        if (isActive) {
          setIsTransactionLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [id, transaction]);

  const handleClose = () => {
    triggerImpactHaptic('light');

    if (isSheet) {
      navigate(-1);
      return;
    }

    navigate('/home', { replace: true });
  };

  const header = (
    <header className="transaction-detail__header">
      {isSheet ? (
        <>
          <h2 id="transaction-detail-title" className="transaction-detail__title">
            {history.transaction}
          </h2>
          <button
            type="button"
            className="transaction-detail__close"
            aria-label={common.close}
            onClick={handleClose}
          >
            <CrossIcon className="transaction-detail__close-icon" />
          </button>
        </>
      ) : (
        <button type="button" className="transaction-detail__back" onClick={handleClose}>
          <LeftArrowIcon className="transaction-detail__back-icon" />
          <span id="transaction-detail-title" className="transaction-detail__title">
            {history.transaction}
          </span>
        </button>
      )}
    </header>
  );

  if (!transaction) {
    const missingContent = (
      <>
        {header}
        {isTransactionLoading ? (
          <TransactionDetailSkeleton label={history.loading} />
        ) : (
          <p className="transaction-detail__missing">{history.transactionNotFound}</p>
        )}
      </>
    );

    return isSheet ? (
      <BottomSheet
        className="transaction-detail"
        panelClassName="transaction-detail__panel"
        titleId="transaction-detail-title"
        onClose={handleClose}
      >
        {missingContent}
      </BottomSheet>
    ) : (
      <section className="transaction-detail transaction-detail--page">
        <div className="transaction-detail__page-content">
          {missingContent}
        </div>
      </section>
    );
  }

  const txHash = getTransactionHash(transaction);
  const blockscannerUrl = getBlockscannerUrl(transaction);
  const isTopUp = isTopUpTransaction(transaction);
  const isPaymentCanceled = isPaymentCanceledTransaction(transaction);
  const shouldShowMerchantNameInfo = ['brand', 'refund', 'payment-canceled']
    .includes(transaction.iconVariant);
  const shouldShowReference = !isTopUp && transaction.reference;
  const amountParts = String(transaction.amountFormatted || '')
    .trim()
    .match(/^([+\-−])\s*(.*)$/);
  const amountSign = isPaymentCanceled
    ? ''
    : amountParts?.[1] || (transaction.amount > 0 ? '+' : '-');
  const amountValue = amountParts?.[2] || transaction.amountFormatted;
  const statusLabel = getStatusLabel(transaction, history);
  const amountClassName = [
    'transaction-detail__amount',
    transaction.amount > 0 && 'transaction-detail__amount--positive',
    isPaymentCanceled && 'transaction-detail__amount--canceled',
    !statusLabel && 'transaction-detail__amount--without-status',
  ].filter(Boolean).join(' ');
  const statusClassName = [
    'transaction-detail__status',
    isPaymentCanceled && 'transaction-detail__status--canceled',
  ].filter(Boolean).join(' ');
  const explorerLabel = transaction.explorerName
    ? t('deposit.viewOnExplorer', { explorer: transaction.explorerName })
    : deposit.viewOnBlockExplorer;

  const content = (
    <>
      {header}

      <div className="transaction-detail__hero">
        <TransactionIcon transaction={transaction} className="transaction-detail__icon" />
        <p className="transaction-detail__merchant">
          {getLocalizedTransactionText(transaction.merchant, history)}
        </p>
        {statusLabel ? (
          <span className={statusClassName}>{statusLabel}</span>
        ) : null}
        <div
          data-amount-sign={amountSign}
          className={amountClassName}
        >
          {amountSign ? (
            <span className="transaction-detail__amount-sign">
              {amountSign}
            </span>
          ) : null}
          <span className="transaction-detail__amount-value">{amountValue}</span>
        </div>
      </div>

      <div className="transaction-detail__card">
        <DetailRow
          label={history.date}
          value={formatTransactionFullDate(transaction, language)}
        />
        {!isTopUp && transaction.originalAmountFormatted ? (
          <DetailRow
            label={isPaymentCanceled ? history.amount : history.paidAmount}
            value={transaction.originalAmountFormatted}
          />
        ) : null}
        {!isTopUp && transaction.exchangeFormatted ? (
          <DetailRow label={history.exchange} value={transaction.exchangeFormatted} />
        ) : null}
        {transaction.cryptoAsset ? (
          <DetailRow
            label={history.sent}
            value={(
              <CurrencyValue
                code={transaction.cryptoAsset}
                iconUrl={transaction.iconUrl}
                network={transaction.network}
              />
            )}
          />
        ) : null}
        {transaction.cardLast4 ? (
          <DetailRow
            label={history.cardLabel}
            value={(
              <TransactionCardIdentity
                transaction={transaction}
                variant="inline"
                showName
              />
            )}
          />
        ) : null}
        <DetailRow
          label={history.category}
          value={getLocalizedTransactionCategory(transaction, history)}
        />
        {txHash ? (
          <DetailRow
            label={history.txHash}
            value={
              blockscannerUrl ? (
                <button
                  type="button"
                  className="transaction-detail__link"
                  onClick={() => openExternalLink(blockscannerUrl)}
                >
                  {formatTxHash(txHash)}
                </button>
              ) : (
                formatTxHash(txHash)
              )
            }
          />
        ) : null}
        {shouldShowReference ? <DetailRow label={history.reference} value={transaction.reference} /> : null}
      </div>

      {shouldShowMerchantNameInfo ? (
        <MerchantNameInfo
          key={transaction.id}
          originalDescription={transaction.originalDescription}
        />
      ) : null}

      {blockscannerUrl ? (
        <button
          type="button"
          className="transaction-detail__explorer"
          onClick={() => openExternalLink(blockscannerUrl)}
        >
          <span>{explorerLabel}</span>
          <RightArrowIcon className="transaction-detail__explorer-icon" />
        </button>
      ) : null}
    </>
  );

  return isSheet ? (
    <BottomSheet
      className="transaction-detail"
      panelClassName="transaction-detail__panel"
      titleId="transaction-detail-title"
      onClose={handleClose}
    >
      {content}
    </BottomSheet>
  ) : (
    <section className="transaction-detail transaction-detail--page">
      <div className="transaction-detail__page-content">
        {content}
      </div>
    </section>
  );
}

export default TransactionDetail;
